import type { HelpGuide } from '../types';

export const cipGuide: HelpGuide = {
  key: 'cip',
  title: 'งานระหว่างก่อสร้าง (CIP) — TFRS 23',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ งานระหว่างก่อสร้าง (CIP)',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/cip/คู่มือการใช้งาน_งานระหว่างก่อสร้าง-CIP_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “งานระหว่างก่อสร้าง — CIP Management + TFRS 23” ใช้คุมต้นทุนของโครงการที่ยังก่อสร้าง/ติดตั้งไม่เสร็จ โดยมองแต่ละโครงการเป็น “ถังเก็บต้นทุน (Cost Container)” ที่สะสมต้นทุนทั้งหมดไว้ในบัญชี 10260002 — งานระหว่างก่อสร้าง จนกว่าสินทรัพย์จะพร้อมใช้งาน แล้วจึงโอนออกไปตั้งเป็นทรัพย์สินถาวร (FA)',
        },
        {
          kind: 'callout',
          text: 'วงจรการบันทึก — Project as Cost Container: (1) สร้าง Project + ผูกบัญชี 10260002 → (2) สะสม Direct + Indirect Cost → (3) Auto Capitalize ดอกเบี้ย (TFRS 23) → (4) โอนเป็นทรัพย์สิน เมื่อพร้อมใช้งาน',
        },
        { kind: 'para', text: 'หน้าจอมี 6 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ทะเบียนโครงการ (Project Register) — ',
              text: 'รายการโครงการ CIP ทั้งหมด + ตัวกรอง + KPI (แท็บเริ่มต้น)',
            },
            {
              lead: 'รายละเอียดรายโครงการ — ',
              text: 'ดูโครงการเดียว: KPI หัวเรื่อง, แยกต้นทุนตาม Cost Pool, การ์ดเงินประกันผลงาน และตารางต้นทุนสะสม',
            },
            {
              lead: 'กล่องรับเข้า (AP) — ',
              text: 'รายการ CIP_CLEARING ที่มาจากใบสำคัญตั้งหนี้ (AP) รอ “รับเข้าโครงการ”',
            },
            {
              lead: 'คำนวณดอกเบี้ย TFRS 23 — ',
              text: 'ตารางเงินกู้ + Cap Rate + จัดสรรดอกเบี้ยที่ Capitalize + หมายเหตุเปิดเผย IAS 23.26',
            },
            {
              lead: 'โอนเป็นทรัพย์สิน (Capitalization) — ',
              text: 'โครงการที่พร้อม/ใกล้พร้อมโอนออกไปตั้งเป็นทรัพย์สินถาวร',
            },
            {
              lead: 'CIP Rollforward — สรุปยกยอด — ',
              text: 'ความเคลื่อนไหวระหว่างงวด (ยกมา + เพิ่ม − โอนออก − ตัดจำหน่าย = คงเหลือ)',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/cip/01-overview-register.png',
          caption:
            'ภาพรวม — KPI 4 ใบ (CIP คงเหลือ/Borrowing Cost/พร้อมโอน/งบประมาณ), แถบวงจร 4 ขั้น และตารางทะเบียนโครงการ',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบนสุด:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'CIP คงเหลือในบัญชี 10260002 — ',
              text: 'ยอดต้นทุนงานระหว่างก่อสร้างที่ยังคงค้างในบัญชี 10260002 พร้อมจำนวนโครงการที่กำลังก่อสร้าง',
            },
            {
              lead: 'Borrowing Cost ใน CIP (สะสม) — ',
              text: 'ดอกเบี้ยที่ Capitalize เข้า CIP สะสม + สัดส่วน % ของ CIP (TFRS 23)',
            },
            {
              lead: 'พร้อมโอนเป็นทรัพย์สิน — ',
              text: 'จำนวนโครงการที่พร้อมโอน + มูลค่ารวมที่จะโอน',
            },
            {
              lead: 'งบประมาณรวม — ',
              text: 'งบประมาณรวมทุกโครงการ + % ที่ใช้ไปเทียบกับงบเฉลี่ย',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: เลือกปี (ปี: 2026/2025) · คำนวณดอกเบี้ยที่ Capitalize · Export CIP Schedule · สร้าง Project ใหม่ (ปุ่มหลัก) ตารางทะเบียนกรองด้วยช่องค้นหา รหัส/ชื่อโครงการ/สาขา + ตัวกรองสถานะ/สาขา + เช็กบ็อกซ์ “เฉพาะเกินกำหนด (Stale)” คอลัมน์: Project Code · ชื่อโครงการ · สาขา/ที่ตั้ง · Project Manager · งบประมาณ · CIP สะสม (10260002) · ความคืบหน้า · วันใช้งานสะสม · คาดเสร็จ · สถานะ · การกระทำ — คลิกแถวเพื่อเข้าดูรายละเอียดโครงการ',
        },
      ],
    },
    {
      id: 'new-project',
      heading: '3. การสร้างโครงการ CIP ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม สร้าง Project ใหม่ แล้วกรอกข้อมูลในฟอร์ม “สร้าง Project งานระหว่างก่อสร้างใหม่”: ชื่อโครงการ · รหัสโครงการ (สั้น) · สาขา · หน่วยงาน · Project Manager · วันเริ่ม · คาดเสร็จ · งบประมาณ (บาท)',
        },
        {
          kind: 'para',
          text: 'ช่อง นโยบายดอกเบี้ย (TFRS 23) เลือกได้ 3 แบบ: “ไม่ Capitalize ดอกเบี้ย” / “Specific — เงินกู้เฉพาะ” / “General Pool” หากเลือกแบบ Specific จะมีช่องให้เลือก เงินกู้เฉพาะ ที่ผูกกับโครงการนี้ เสร็จแล้วกด สร้าง Project',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'นโยบายดอกเบี้ยกำหนดว่าโครงการจะถูกคิดดอกเบี้ยเข้าทุน (Capitalize) อย่างไรในแท็บคำนวณดอกเบี้ย TFRS 23 — Specific ใช้อัตราของเงินกู้ที่ผูกไว้ ส่วน General Pool ใช้ Cap Rate ถัวเฉลี่ยของเงินกู้ทั่วไป',
        },
        {
          kind: 'image',
          src: '/help/cip/02-new-project.png',
          caption:
            'ฟอร์มสร้าง Project ใหม่ — ชื่อ/รหัส/สาขา/หน่วยงาน/PM/วันเริ่ม-คาดเสร็จ/งบประมาณ และนโยบายดอกเบี้ย TFRS 23',
        },
      ],
    },
    {
      id: 'detail',
      heading: '4. รายละเอียดรายโครงการ',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกที่แถวโครงการ (หรือปุ่ม ดูต้นทุน) เพื่อเข้าแท็บ รายละเอียดรายโครงการ ส่วนหัวแสดง KPI ของโครงการ (งบประมาณ/CIP สะสม/งบคงเหลือ/ความคืบหน้า) ถัดมาเป็นแถบแยกต้นทุนตาม Cost Pool 5 กอง: Direct — เครื่องจักร/อุปกรณ์ · Direct — ก่อสร้าง/วัสดุ · Indirect — ดูแลโครงการ · Borrowing Cost (TFRS 23) · อื่นๆ (ทดสอบ ขนส่ง อนุญาต)',
        },
        {
          kind: 'para',
          text: 'การ์ด เงินประกันผลงาน (Retention) แสดงยอดที่หักไว้/คืนแล้ว/คงค้าง พร้อมปุ่ม คืนเงินประกัน ด้านล่างเป็นตารางต้นทุนสะสม: ลำดับ · วันที่ · JV/Ref · รายการ · Vendor · Cost Pool · จำนวนเงิน โดยมีบรรทัดท้ายตารางยืนยันว่ายอดสะสมตรงกับยอดในบัญชี 10260002',
        },
        {
          kind: 'para',
          text: 'ปุ่มจัดการโครงการที่หัวแท็บ (ขึ้นกับสถานะ): ทำเครื่องหมายพร้อมใช้งาน · ระงับชั่วคราว · กลับมาทำต่อ · ตัดจำหน่าย (Write-off) · โอนเป็นทรัพย์สิน',
        },
        {
          kind: 'image',
          src: '/help/cip/03-detail.png',
          caption:
            'รายละเอียดรายโครงการ — KPI หัวเรื่อง, แถบแยก Cost Pool 5 กอง, การ์ดเงินประกันผลงาน และตารางต้นทุนสะสม',
        },
      ],
    },
    {
      id: 'add-cost',
      heading: '5. การเพิ่มต้นทุนเข้าโครงการ',
      blocks: [
        {
          kind: 'para',
          text: 'ในแท็บรายละเอียด กดปุ่ม เพิ่มต้นทุน เพื่อเปิดฟอร์ม “เพิ่มต้นทุนเข้าโครงการ” กรอก: วันที่ · จำนวนเงิน (บาท) · คำอธิบายรายการ · กองต้นทุน (Cost Pool) · รหัสบัญชีต้นทุน/ค่าใช้จ่าย · ผู้ขาย/ผู้รับเหมา · เลขที่อ้างอิง (PV/สัญญา) และเลือกเช็กบ็อกซ์ว่ารวมเป็นราคาทุนทรัพย์สินหรือไม่',
        },
        {
          kind: 'para',
          text: 'เปิดส่วน “ผู้รับเหมา — หัก ณ ที่จ่าย / เงินประกันผลงาน” เพื่อกรอกภาษีและการหักเงิน มีปุ่มลัดช่วยคำนวณ: VAT 7% · หัก ณ ที่จ่าย 3% · เงินประกันผลงาน 5% ระบบแสดงภาษีซื้อ (VAT) · ภาษีหัก ณ ที่จ่าย (WHT) · เงินประกันผลงาน (Retention) พร้อมแถบยอดจ่ายสุทธิให้ผู้รับเหมา เสร็จแล้วกด บันทึกต้นทุน',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'มีเพียง “จำนวนเงิน” (มูลค่าต้นทุน) เท่านั้นที่ถูกบันทึกเข้าทุน CIP — VAT, WHT และเงินประกันผลงานจะถูกแยกไปยังบัญชีที่เกี่ยวข้อง ไม่รวมเป็นทุนของสินทรัพย์',
        },
        {
          kind: 'image',
          src: '/help/cip/04-add-cost-entry.png',
          caption:
            'ฟอร์มเพิ่มต้นทุนเข้าโครงการ — ปุ่มลัด VAT 7% / หัก ณ ที่จ่าย 3% / เงินประกันผลงาน 5% และแถบยอดจ่ายสุทธิ',
        },
      ],
    },
    {
      id: 'inbox',
      heading: '6. กล่องรับเข้า (AP)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ กล่องรับเข้า (AP) รวมรายการต้นทุนที่บันทึกผ่านใบสำคัญตั้งหนี้ (AP) แล้วถูกพักไว้ในบัญชี CIP_CLEARING รอนำเข้าโครงการ ตารางแสดง: วันที่ · เลขที่ใบสำคัญ · ผู้ขาย/ผู้รับเงิน · โครงการ · จำนวนเงิน · การกระทำ',
        },
        {
          kind: 'para',
          text: 'แต่ละแถวกดปุ่ม รับเข้าโครงการ เพื่อย้ายต้นทุนจาก CIP_CLEARING เข้าเป็นต้นทุนสะสมของโครงการ (ระหว่างทำงานปุ่มจะแสดง “กำลังรับเข้า…”) วิธีนี้ช่วยให้ต้นทุนที่ตั้งหนี้ฝั่ง AP ไหลเข้าโครงการได้อัตโนมัติโดยไม่ต้องคีย์ซ้ำ',
        },
        {
          kind: 'image',
          src: '/help/cip/05-inbox-ap.png',
          caption:
            'กล่องรับเข้า (AP) — รายการ CIP_CLEARING จากใบสำคัญ AP รอกดปุ่ม “รับเข้าโครงการ”',
        },
      ],
    },
    {
      id: 'borrowing',
      heading: '7. คำนวณดอกเบี้ย TFRS 23',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ คำนวณดอกเบี้ย TFRS 23 ใช้คิดดอกเบี้ยเงินกู้เข้าเป็นต้นทุนของโครงการที่ยังก่อสร้างไม่เสร็จ (Capitalize) แสดงตารางเงินกู้ (เลขที่ · ธนาคาร/สัญญา · เงินต้น · ดอกเบี้ย/ปี · วัตถุประสงค์) และอัตรา Capitalization Rate (Cap Rate) ที่ถัวเฉลี่ยจากเงินกู้ทั่วไป',
        },
        {
          kind: 'para',
          text: 'ตารางจัดสรรแสดงรายโครงการ: เงินกู้อ้างอิง · CIP เฉลี่ยเดือนนี้ · Capitalization Rate · ดอกเบี้ยที่ Capitalize · บัญชี Cr. · สถานะ และการ์ดเปิดเผยข้อมูล IAS 23.26 (งวด · Cap Rate · ดอกเบี้ยที่รวมเป็นทุน) เพื่อนำไปทำหมายเหตุประกอบงบ',
        },
        {
          kind: 'para',
          text: 'เมื่อพร้อมลงบัญชี กดปุ่ม คำนวณดอกเบี้ยที่ Capitalize (มุมขวาบน) เพื่อเปิดหน้าต่างสรุปดอกเบี้ยรวมที่จะ Capitalize ของงวด แล้วกด ส่งอนุมัติ (4-eyes) เพื่อส่งเข้ากระบวนการอนุมัติ — ใบสำคัญจะลงบัญชีก็ต่อเมื่อได้รับอนุมัติครบขั้น',
        },
        {
          kind: 'image',
          src: '/help/cip/06-borrowing-tfrs23.png',
          caption:
            'คำนวณดอกเบี้ย TFRS 23 — ตารางเงินกู้ (Cap Rate), การจัดสรรดอกเบี้ยที่ Capitalize และการ์ดเปิดเผย IAS 23.26',
        },
      ],
    },
    {
      id: 'capitalize',
      heading: '8. การโอนเป็นทรัพย์สิน (Capitalization)',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อโครงการพร้อมใช้งาน กดปุ่ม โอนเป็นทรัพย์สิน (จากแถวในแท็บ Capitalization กด เริ่มโอน → หรือจากแท็บรายละเอียด) ระบบเปิดตัวช่วย 3 ขั้นตอน: (1) ทบทวนต้นทุน — ทบทวนต้นทุนแยกตาม Cost Pool · (2) แบ่งเป็นทรัพย์สิน — แบ่งต้นทุนรวมเป็นหลายรายการทรัพย์สิน (ระบุชื่อ/หมวด/อายุ(ปี)/มูลค่า โดยมีแถบผลต่างตรวจให้ลงตัว) · (3) ยืนยันและสร้าง JV',
        },
        {
          kind: 'para',
          text: 'ขั้นสุดท้ายแสดงตัวอย่างใบสำคัญ (JV): Dr 10220001 ทรัพย์สินถาวร / Cr 10260002 งานระหว่างก่อสร้าง แล้วกด ส่งคำขออนุมัติ (4-eyes) ใบสำคัญและการตั้งทรัพย์สินจะเกิดขึ้นจริงเมื่อผู้มีอำนาจอนุมัติครบขั้นตอนเท่านั้น',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'หลังส่งคำขอ ระบบจะแสดงไทม์ไลน์การอนุมัติ (ApprovalTimeline) และปุ่ม พิจารณา ให้ผู้มีอำนาจกดอนุมัติ/ตีกลับ/ไม่อนุมัติ ผ่านหน้าต่าง “พิจารณาการโอนเป็นทรัพย์สิน”',
        },
        {
          kind: 'image',
          src: '/help/cip/07-capitalize-jv-preview.png',
          caption:
            'โมดัลโอนเป็นทรัพย์สิน — ตัวอย่าง JV (Dr 10220001 FA / Cr 10260002 CIP) และปุ่ม “ส่งคำขออนุมัติ (4-eyes)”',
        },
      ],
    },
    {
      id: 'rollforward',
      heading: '9. CIP Rollforward — สรุปยกยอด',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ CIP Rollforward — สรุปยกยอด ใช้ตรวจความเคลื่อนไหวของ CIP ในแต่ละงวด (สำหรับปิดงวด) เลือกงวดที่ต้องการ ตารางแสดงรายโครงการ: Project Code · ชื่อโครงการ · สาขา · ยอดยกมา · เพิ่มระหว่างงวด · โอนออก (Capitalized) · ตัดจำหน่าย · ยอดคงเหลือ',
        },
        {
          kind: 'para',
          text: 'สูตรตรวจสอบ (แสดงที่ท้ายตาราง): ยอดคงเหลือ = ยอดยกมา + เพิ่มระหว่างงวด − โอนออก − ตัดจำหน่าย (บัญชี 10260002) ใช้กระทบยอดกับบัญชีแยกประเภทได้ทันที และกด Export Excel เพื่อนำออกไปจัดทำกระดาษทำการ',
        },
        {
          kind: 'image',
          src: '/help/cip/08-rollforward.png',
          caption:
            'CIP Rollforward — ตารางยกยอด opening / เพิ่มระหว่างงวด / โอนออก / ตัดจำหน่าย / คงเหลือ',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '10. สรุปสถานะของโครงการ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['วางแผน', 'สร้างโครงการแล้วแต่ยังไม่เริ่มก่อสร้าง'],
            ['กำลังก่อสร้าง', 'โครงการที่กำลังสะสมต้นทุน (เพิ่มต้นทุน/ระงับได้)'],
            ['ระงับชั่วคราว', 'หยุดคิดดอกเบี้ยเข้าทุนชั่วคราว (IAS 23.20) — กลับมาทำต่อได้'],
            ['พร้อมตั้งทรัพย์สิน', 'พร้อมโอนออกไปตั้งเป็นทรัพย์สินถาวร'],
            ['โอนเป็นทรัพย์สินแล้ว', 'โอนต้นทุน CIP ออกไปตั้งเป็นทรัพย์สินถาวร (FA) เรียบร้อย'],
          ],
        },
        {
          kind: 'para',
          tone: 'muted',
          text: 'หมายเหตุ: คำขอ Capitalize ดอกเบี้ย และคำขอโอนเป็นทรัพย์สิน มีสถานะอนุมัติแยกต่างหาก เช่น รออนุมัติ · ตีกลับแก้ไข · ไม่อนุมัติ · อนุมัติแล้ว',
        },
      ],
    },
    {
      id: 'faq',
      heading: '11. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมต้นทุนจาก AP ไม่ขึ้นในโครงการทันที?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ต้นทุนที่ตั้งหนี้ผ่าน AP จะพักในบัญชี CIP_CLEARING ก่อน ต้องไปที่แท็บ กล่องรับเข้า (AP) แล้วกด รับเข้าโครงการ จึงจะเข้าเป็นต้นทุนสะสม',
            },
          ],
        },
        { kind: 'para', text: 'ดอกเบี้ยเข้าทุน (TFRS 23) บันทึกอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ที่แท็บ คำนวณดอกเบี้ย TFRS 23 กดปุ่ม คำนวณดอกเบี้ยที่ Capitalize แล้ว ส่งอนุมัติ (4-eyes) เมื่ออนุมัติครบขั้นระบบจึงลงใบสำคัญ',
            },
          ],
        },
        { kind: 'para', text: 'กดปุ่ม “โอนเป็นทรัพย์สิน” แล้วทรัพย์สินยังไม่ถูกตั้ง?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'การโอนเป็นกระบวนการ 4-eyes — JV (Dr 10220001 / Cr 10260002) และการตั้งทรัพย์สินจะเกิดเมื่อผู้มีอำนาจอนุมัติครบขั้นตอนเท่านั้น',
            },
          ],
        },
        { kind: 'para', text: 'CIP ในรายงานไม่ตรงกับบัญชี 10260002?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ใช้แท็บ CIP Rollforward — สรุปยกยอด กระทบยอดตามสูตร ยอดคงเหลือ = ยอดยกมา + เพิ่ม − โอนออก − ตัดจำหน่าย',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '12. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'CIP — ',
              text: 'งานระหว่างก่อสร้าง (Construction in Progress) ต้นทุนของสินทรัพย์ที่ยังสร้าง/ติดตั้งไม่เสร็จ สะสมในบัญชี 10260002',
            },
            {
              lead: 'TFRS 23 / IAS 23 — ',
              text: 'มาตรฐานเรื่องต้นทุนการกู้ยืม กำหนดให้รวมดอกเบี้ยเข้าเป็นทุนของสินทรัพย์ที่เข้าเงื่อนไข',
            },
            {
              lead: 'Cost Pool — ',
              text: 'กองต้นทุนที่ใช้แยกประเภทต้นทุน (Direct เครื่องจักร/ก่อสร้าง, Indirect, Borrowing Cost, อื่นๆ)',
            },
            {
              lead: 'Borrowing Cost — ',
              text: 'ดอกเบี้ยเงินกู้ที่ Capitalize เข้าเป็นต้นทุนของโครงการ',
            },
            {
              lead: 'Capitalization Rate (Cap Rate) — ',
              text: 'อัตราถัวเฉลี่ยของเงินกู้ทั่วไป ใช้คิดดอกเบี้ยเข้าทุนกรณีนโยบาย General Pool',
            },
            {
              lead: 'CIP_CLEARING — ',
              text: 'บัญชีพักต้นทุนจากใบสำคัญ AP รอ “รับเข้าโครงการ”',
            },
            {
              lead: 'เงินประกันผลงาน (Retention) — ',
              text: 'เงินที่หักไว้จากผู้รับเหมาเพื่อประกันงาน จ่ายคืนเมื่อครบเงื่อนไข',
            },
            {
              lead: 'Capitalize / โอนเป็นทรัพย์สิน — ',
              text: 'การโอนต้นทุน CIP ออกจากบัญชี 10260002 ไปตั้งเป็นทรัพย์สินถาวร (Dr 10220001 / Cr 10260002)',
            },
            {
              lead: '4-eyes — ',
              text: 'การควบคุมแบบผู้ทำ–ผู้อนุมัติ ต้องมีผู้อนุมัติครบขั้นก่อนลงบัญชีจริง',
            },
          ],
        },
      ],
    },
  ],
};
